import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Flex } from "antd";
import QuestionnaireButtonFooterV2 from "components/basic/Footer/QuestionnaireButtonFooterV2";
import ButtonLoading from "components/basic/Spinner/ButtonLoading";
import { useModalOpener } from "hooks/helpers/useModalOpener";
import { useAppSelector } from "redux/hooks";
import { selectCurrentNavigation } from "redux/reducers/navigation";
import { IUser } from "types/auth";
import { checkDepends } from "utils/new-questionnaire";

import { Depend, Step } from "../../constants/questions";
import { PromptModalType, QuestionType } from "../../enums/enum";
import AddressQuestion from "../AddressQuestion/AddressQuestion";
import AmountQuestion from "../AmountQuestion/AmountQuestion";
import ArrayQuestionV3 from "../ArrayQuestionV3/ArrayQuestionV3";
import DateQuestion from "../DateQuestion/DateQuestion";
import NinUtrQuestion from "../NinUtrQuestion/NinUtrQuestion";
import ObjectQuestion from "../ObjectQuestion/ObjectQuestion";
import PercentageQuestion from "../PercentageQuestion/PercentageQuestion";
import ProfileQuestion from "../ProfileQuestion/ProfileQuestion";
import QuestionnaireStepQuestionsList from "../QuestionnaireStepQuestionsList/QuestionnaireStepQuestionsList";
import PrimaryIncomeQuestion from "../SelectManyOptionIncomeQuestion/PrimaryIncomeQuestion";
import SelectManyOptionQuestion from "../SelectManyOptionQuestion/SelectManyOptionQuestion";
import SelectOneIconQuestion from "../SelectOneIconQuestion/SelectOneIconQuestion";
import SelectOneOptionQuestion from "../SelectOneOptionQuestion/SelectOneOptionQuestion";
import SelectOneTilesQuestion from "../SelectOneTilesQuestion/SelectOneTilesQuestion";
import PromptModal from "../Shared/PromptModal";
import SwipeableCards from "../SwipeableCards/SwipeableCards";
import TextQuestion from "../TextQuestion/TextQuestion";
import UploadFileQuestion from "../UploadFileQuestion/UploadFileQuestion";

type QuestionnaireStepProps = {
    currentStepData: Step;
    handlePrev: () => void;
    categoryAnswers: Record<string, any>;
    handleAnswerChange: (_question: string, _answer: any) => void;
    allQuestionsAnswered: boolean;
    handleStepContinue: (_questionType?: QuestionType) => void;
    allProfilesCompleted: boolean;
    user: IUser;
    refetchTaxProfiles?: () => void;
    refetchConnectedBankAccounts?: () => void;
};

const QuestionnaireStepMobile: React.FC<QuestionnaireStepProps> = ({
    currentStepData,
    handlePrev,
    handleAnswerChange,
    categoryAnswers = {},
    allQuestionsAnswered,
    handleStepContinue,
    allProfilesCompleted,
    user,
    refetchTaxProfiles,
    refetchConnectedBankAccounts,
}) => {
    const { stepLoading } = useAppSelector(selectCurrentNavigation);

    const [hideContinue, setHideContinue] = useState(false);
    const [continueDisabled, setContinueDisabled] = useState(false);
    const [continueButtonText, setContinueButtonText] = useState("Next");
    const [hideTitle, setHideTitle] = useState(false);
    const [modalHasBeenOpened, setModalHasBeenOpened] = useState(false);
    const [remount, setRemount] = useState(false);

    const isProfileStep = !!currentStepData.parentProfileType;
    const canContinue = isProfileStep
        ? currentStepData.allowContinue || allProfilesCompleted
        : currentStepData.allowContinue || allQuestionsAnswered;

    const buttonDisabled = stepLoading || continueDisabled || currentStepData.hasCards || !canContinue;

    const promptModalData = useMemo(() => ({
        type: currentStepData.promptModal?.type || PromptModalType.info,
        title: currentStepData.promptModal?.title || "",
        text: currentStepData.promptModal?.text || "",
        image: currentStepData.promptModal?.image || "",
    }), [currentStepData?.promptModal]);

    const {
        isOpened: isStepHelpModalOpened,
        open: handleStepHelpOpenModal,
        close: handleStepHelpCloseModal,
    } = useModalOpener();

    const {
        isOpened: isConfirmContinueModalOpened,
        open: handleConfirmContinueOpenModal,
        close: handleConfirmContinueCloseModal,
    } = useModalOpener();

    const checkModalDepends = useCallback((depend: Depend[], categoryAnswers?: Record<string, any>) => {
        if (categoryAnswers) {
            return checkDepends(depend, categoryAnswers);
        } else {
            return false;
        }
    }, []);

    const showModalOpener = useMemo(() => {
        if (currentStepData?.promptModal?.depend && categoryAnswers) {
            return checkModalDepends(currentStepData.promptModal?.depend, categoryAnswers);
        }
        return false;
    }
    , [currentStepData?.promptModal?.depend, categoryAnswers, checkModalDepends]);

    const handleNextClick = () => {
        if (isProfileStep) {
            const questionKey = currentStepData.questions?.[0]?.question;
            const catAnswers = questionKey ? categoryAnswers[questionKey] : [];
            if (!catAnswers?.length) {
                handleConfirmContinueOpenModal();
                return;
            }
        }
        handleStepContinue();
        if (!isProfileStep) setContinueButtonText("Next");
    };

    useEffect(() => {
        if (currentStepData?.promptModal?.depend && categoryAnswers) {
            if (showModalOpener && !currentStepData.promptModal?.manualOpen && !modalHasBeenOpened) {
                handleStepHelpOpenModal();
                setModalHasBeenOpened(true);
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryAnswers, showModalOpener]);

    useEffect(() => {
        setRemount(true);
        const timer = setTimeout(() => {
            setRemount(false);
        }, 1);
        return () => clearTimeout(timer);
    }, [currentStepData]);

    return (
        <>
            {!remount && currentStepData?.hasCards && (
                <SwipeableCards
                    categoryAnswers={categoryAnswers}
                    cards={currentStepData.cards || []}
                    title={currentStepData.title}
                    description={currentStepData.description}
                    onAnswerChange={handleAnswerChange}
                    showModalOpener={showModalOpener}
                    handleStepHelpOpenModal={handleStepHelpOpenModal}
                />
            )}
            {currentStepData?.questions && (
                <div className="w100">
                    {!hideTitle && <div className="new-questionnaire__form-title taLeft" style={{ margin: "20px auto 0px" }}>{currentStepData.title}</div>}
                    {currentStepData.questions.map((question, idx) => {
                        if (question.depend?.length && !checkDepends(question.depend, categoryAnswers)) {
                            return null;
                        }
                        return (
                            <div key={idx}>
                                {question.type === QuestionType.amountQuestion && (
                                    <AmountQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        setContinueButtonText={setContinueButtonText}
                                        handlePrev={handlePrev}
                                        initialValue={categoryAnswers[question.question]}
                                        categoryAnswers={categoryAnswers}
                                    />
                                )}
                                {question.type === QuestionType.percentageQuestion && (
                                    <PercentageQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                    />
                                )}
                                {question.type === QuestionType.textQuestion && (
                                    <TextQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                    />
                                )}
                                {question.type === QuestionType.addressQuestion && (
                                    <AddressQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        setContinueDisabled={setContinueDisabled}
                                    />
                                )}
                                {question.type === QuestionType.dateQuestion && (
                                    <DateQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                    />
                                )}
                                {question.type === QuestionType.selectOneIconQuestion && (
                                    <SelectOneIconQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        handlePrev={handlePrev}
                                        setHideContinue={setHideContinue}
                                        handleContinue={handleStepContinue}
                                        initialValue={categoryAnswers[question.question]}
                                        categoryAnswers={categoryAnswers}
                                    />
                                )}
                                {question.type === QuestionType.ninUtrQuestion && (
                                    <div className="new-questionnaire__form">
                                        <NinUtrQuestion
                                            data={question}
                                            onAnswer={handleAnswerChange}
                                            categoryAnswers={categoryAnswers}
                                        />
                                    </div>
                                )}
                                {question.type === QuestionType.selectOneOptionQuestion && (
                                    <SelectOneOptionQuestion
                                        user={user}
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        handleContinue={handleStepContinue}
                                        categoryAnswers={categoryAnswers}
                                    />
                                )}
                                {question.type === QuestionType.selectManyQuestion && (
                                    <SelectManyOptionQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        categoryAnswers={categoryAnswers}
                                        handlePrev={handlePrev}
                                    />
                                )}
                                {question.type === QuestionType.selectOneTilesQuestion && (
                                    <SelectOneTilesQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        categoryAnswers={categoryAnswers}
                                        handlePrev={handlePrev}
                                    />
                                )}
                                {question.type === QuestionType.primaryIncomeQuestion && (
                                    <PrimaryIncomeQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        categoryAnswers={categoryAnswers}
                                        handlePrev={handlePrev}
                                        setContinueButtonText={setContinueButtonText}
                                    />
                                )}
                                {question.type === QuestionType.profileQuestion && (
                                    <ProfileQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        setHideContinue={setHideContinue}
                                        setHideTitle={setHideTitle}
                                        user={user}
                                        refetchTaxProfiles={refetchTaxProfiles}
                                        currentStepData={currentStepData}
                                        refetchConnectedBankAccounts={refetchConnectedBankAccounts}
                                        handlePrev={handlePrev}
                                    />
                                )}
                                {question.type === QuestionType.arrayQuestionV3 && (
                                    <ArrayQuestionV3
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        setHideContinue={setHideContinue}
                                        setHideTitle={setHideTitle}
                                        user={user}
                                        setContinueDisabled={setContinueDisabled}
                                        handlePrev={handlePrev}
                                    />
                                )}
                                {question.type === QuestionType.uploadFileQuestion && (
                                    <UploadFileQuestion
                                        user={user}
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        initialValue={categoryAnswers[question.question]}
                                        refetchTaxProfiles={refetchTaxProfiles}
                                        currentStepData={currentStepData}
                                        categoryAnswers={categoryAnswers}
                                        handlePrev={handlePrev}
                                    />
                                )}
                                {question.type === QuestionType.objectQuestion && (
                                    <ObjectQuestion
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        handlePrev={handlePrev}
                                        initialValue={categoryAnswers[question.question]}
                                        categoryAnswers={categoryAnswers}
                                    />
                                )}
                                {question.type === QuestionType.questionsList && (
                                    <QuestionnaireStepQuestionsList
                                        data={question}
                                        onAnswer={handleAnswerChange}
                                        answers={categoryAnswers}
                                    />
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            {!hideContinue && (
                <div className={"mt100"}>
                    <QuestionnaireButtonFooterV2>
                        <Flex justify="space-between" align="center" className="w100 p20" gap={12}>
                            <button className="shared__secondary-button" onClick={handlePrev}>
                     Back
                            </button>

                            <button
                                className={`shared__primary-button maxWidth160px ${buttonDisabled ? "disabled" : ""}`}
                                onClick={handleNextClick}
                                disabled={buttonDisabled}
                            >
                                {stepLoading ? <ButtonLoading /> : <span>{isProfileStep ? "Next" : continueButtonText}</span>}
                            </button>
                        </Flex>
                    </QuestionnaireButtonFooterV2>
                </div>
            )}

            {isStepHelpModalOpened && (
                <PromptModal
                    isOpen={isStepHelpModalOpened}
                    closeModal={handleStepHelpCloseModal}
                    handlePrev={handlePrev}
                    data={promptModalData}
                />
            )}

            {isConfirmContinueModalOpened && (
                <PromptModal
                    isOpen={isConfirmContinueModalOpened}
                    closeModal={handleConfirmContinueCloseModal}
                    confirmAction={handleStepContinue}
                    handlePrev={() => {}}
                    data={{
                        type: PromptModalType.confirmation,
                        title: "Missing Income Information",
                        text: "<p>It looks like you haven’t provided any income details. If you have income to report, please complete the necessary sections or return to the personal information section and select the relevant income types.</p><p>Continuing without this information may result in an incomplete or inaccurate tax return.</p>",
                        image: "",
                        confirmButtonText: "Continue",
                        cancelButtonText: "Go Back",
                    }}
                />
            )}
        </>
    );
};

export default QuestionnaireStepMobile;
